import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { MapPin, Clock, CheckCircle2, Circle, Upload, Briefcase, MapPinned, Camera, Star, User, Loader2, Save, ExternalLink, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Image, Trash2, Eye, ShieldCheck, ShieldX, ShieldAlert, X, FileText, CalendarCheck, ClipboardCheck, Search } from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { toast } from '../../hooks/use-toast';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { format, differenceInDays, parseISO } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { designationOptions, skillLevels, bloodGroups } from '../../mock';
import api from '../../services/api';
import { DocumentTypeCode, DocumentReferenceType } from '../../constants/stageConstants';

const InstallerJobs = () => {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [incentiveDialogOpen, setIncentiveDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [gpsLocation, setGpsLocation] = useState(null);
  const [capturingGps, setCapturingGps] = useState(false);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const [attendanceProjectId, setAttendanceProjectId] = useState('');
  const [submittingAttendance, setSubmittingAttendance] = useState(false);
  const [myAttendance, setMyAttendance] = useState([]); // table data (browsable month)
  const [currentMonthAttendance, setCurrentMonthAttendance] = useState([]); // always current month for card + clock detection
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceMonth, setAttendanceMonth] = useState(new Date()); // tracks which month to show
  const [totalIncentive, setTotalIncentive] = useState(0);

  // Checklist UI state
  const [checklistData, setChecklistData] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [expandedEntities, setExpandedEntities] = useState({});
  const [expandedUnits, setExpandedUnits] = useState({});
  const [checklistSearch, setChecklistSearch] = useState('');
  const [itemPhotos, setItemPhotos] = useState({});
  const [photosExpanded, setPhotosExpanded] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed' | 'client_review'
  const [viewMode, setViewMode] = useState('jobs'); // 'jobs' | 'reports' | 'attendance'
  const fileInputRef = useRef(null);

  // Client Review state
  const [crJobs, setCrJobs] = useState([]);
  const [crSelectedJob, setCrSelectedJob] = useState(null);
  const [crChecklist, setCrChecklist] = useState(null);
  const [crChecklistLoading, setCrChecklistLoading] = useState(false);
  const [crUpdatingItem, setCrUpdatingItem] = useState(null);
  const [crCompleting, setCrCompleting] = useState(false);
  const [crLocalChanges, setCrLocalChanges] = useState({}); // { responseId: { isCompleted, notes } }
  const [crSaving, setCrSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [completingJob, setCompletingJob] = useState(false);
  // Tracks responseIds whose `notes` were edited locally and need to be persisted on Save Progress.
  // Toggles auto-save immediately, so they don't go in here.
  const [dirtyNoteIds, setDirtyNoteIds] = useState(() => new Set());
  const [crUploadingPhoto, setCrUploadingPhoto] = useState(null);
  const [crItemPhotos, setCrItemPhotos] = useState({});
  const [crPhotosExpanded, setCrPhotosExpanded] = useState({});

  // Profile state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    age: user?.age || '',
    bloodGroup: user?.bloodGroup || '',
    location: user?.location || '',
    skill: user?.skill || '',
    designation: user?.designation || '',
    aadhar: user?.aadhar || '',
    bankAccount: user?.bankAccount || '',
    bankName: user?.bankName || '',
    ifsc: user?.ifsc || ''
  });

  // Progress Report state
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressProjectId, setProgressProjectId] = useState('');
  const [progressDescription, setProgressDescription] = useState('');
  const [progressIssues, setProgressIssues] = useState('');
  const [progressGps, setProgressGps] = useState(null);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [submittingProgress, setSubmittingProgress] = useState(false);
  const [capturingProgressGps, setCapturingProgressGps] = useState(false);
  const [myProgressReports, setMyProgressReports] = useState([]);
  const [loadingMyReports, setLoadingMyReports] = useState(false);
  const [progressReportsExpanded, setProgressReportsExpanded] = useState(false);
  const progressPhotoRef = useRef(null);
  const progressUploadRef = useRef(null);

  // Fetch jobs and counts on mount (parallelized)
  useEffect(() => {
    Promise.all([
      fetchData(),
      fetchProjectCounts(),
      fetchMyAttendance()
    ]);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const jobsData = await api.getInstallerProjects({ status: 'active' });

      // Transform jobs (lightweight — no entities)
      const transformedJobs = jobsData.map(j => {
        const scheduledDate = j.scheduledDate ? new Date(j.scheduledDate) : null;
        return {
          id: j.id,
          projectId: j.projectId || j.id,
          clientName: j.clientName,
          oemName: j.oemName,
          siteAddress: j.siteAddress || 'N/A',
          cityName: j.cityName,
          stateName: j.stateName,
          sqft: j.sqft,
          date: scheduledDate ? format(scheduledDate, 'dd MMM yyyy') : 'Not scheduled',
          time: scheduledDate ? format(scheduledDate, 'hh:mm a') : '',
          deliveryDate: j.estimatedEndDate,
          status: j.status,
          latitude: j.latitude,
          longitude: j.longitude,
          allowProgressReportAnytime: j.allowProgressReportAnytime || false,
          locationValidationRadius: j.locationValidationRadius ?? null,
          supervisorName: j.supervisorName,
          supervisorPhone: j.supervisorPhone,
          team: j.teamName,
          totalItems: j.totalItems || 0,
          completedItems: j.completedItems || 0
        };
      });

      setJobs(transformedJobs);
      if (transformedJobs.length > 0 && !selectedJob) {
        handleSelectJob(transformedJobs[0]);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load jobs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedJobs = async () => {
    try {
      setLoading(true);
      const jobsData = await api.getInstallerProjects({ status: 'completed' });
      const transformedJobs = jobsData.map(j => {
        const scheduledDate = j.scheduledDate ? new Date(j.scheduledDate) : null;
        return {
          id: j.id,
          projectId: j.projectId || j.id,
          clientName: j.clientName,
          oemName: j.oemName,
          siteAddress: j.siteAddress || 'N/A',
          cityName: j.cityName,
          stateName: j.stateName,
          sqft: j.sqft,
          date: scheduledDate ? format(scheduledDate, 'dd MMM yyyy') : 'Not scheduled',
          time: scheduledDate ? format(scheduledDate, 'hh:mm a') : '',
          deliveryDate: j.estimatedEndDate,
          status: j.status,
          latitude: j.latitude,
          longitude: j.longitude,
          allowProgressReportAnytime: j.allowProgressReportAnytime || false,
          supervisorName: j.supervisorName,
          supervisorPhone: j.supervisorPhone,
          team: j.teamName,
          totalItems: j.totalItems || 0,
          completedItems: j.completedItems || 0
        };
      });
      setCompletedJobs(transformedJobs);
    } catch (error) {
      console.error('Error fetching completed jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedJobsCount = async () => {
    try {
      const counts = await api.getInstallerProjectCounts();
      setCompletedJobs(Array(counts.completed || 0).fill({ id: null }));
      setCrJobs(Array(counts.clientReview || 0).fill({ id: null }));
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const fetchProjectCounts = async () => {
    try {
      const counts = await api.getInstallerProjectCounts();
      setCompletedJobs(Array(counts.completed || 0).fill({ id: null }));
      setCrJobs(Array(counts.clientReview || 0).fill({ id: null }));
    } catch (error) {
      console.error('Error fetching project counts:', error);
    }
  };

  const fetchClientReviewJobsCount = async () => {
    await fetchProjectCounts();
  };

  const fetchChecklist = async (projectId) => {
    try {
      setChecklistLoading(true);
      setChecklistData(null);
      setItemPhotos({});
      setPhotosExpanded({});
      const data = await api.getInstallerProjectChecklist(projectId);
      setChecklistData(data);
    } catch (error) {
      console.error('Error fetching checklist:', error);
      toast({ title: 'Error', description: 'Failed to load checklist', variant: 'destructive' });
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    fetchChecklist(job.projectId || job.id);
  };

  const refreshChecklist = async () => {
    if (selectedJob) {
      await fetchChecklist(selectedJob.projectId || selectedJob.id);
      // Also refresh the project list for updated totals
      const jobsData = await api.getInstallerProjects({ status: activeTab });
      const transformedJobs = jobsData.map(j => {
        const scheduledDate = j.scheduledDate ? new Date(j.scheduledDate) : null;
        return {
          id: j.id,
          projectId: j.projectId || j.id,
          clientName: j.clientName,
          oemName: j.oemName,
          siteAddress: j.siteAddress || 'N/A',
          cityName: j.cityName,
          stateName: j.stateName,
          sqft: j.sqft,
          date: scheduledDate ? format(scheduledDate, 'dd MMM yyyy') : 'Not scheduled',
          time: scheduledDate ? format(scheduledDate, 'hh:mm a') : '',
          deliveryDate: j.estimatedEndDate,
          status: j.status,
          latitude: j.latitude,
          longitude: j.longitude,
          locationValidationRadius: j.locationValidationRadius ?? null,
          supervisorName: j.supervisorName,
          supervisorPhone: j.supervisorPhone,
          team: j.teamName,
          totalItems: j.totalItems || 0,
          completedItems: j.completedItems || 0
        };
      });
      if (activeTab === 'active') {
        setJobs(transformedJobs);
      } else {
        setCompletedJobs(transformedJobs);
      }
      const updated = transformedJobs.find(j => j.id === selectedJob.id);
      if (updated) {
        setSelectedJob(updated);
      } else {
        setSelectedJob(null);
      }
    }
  };

  // Haversine distance in meters
  const calcDistanceMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // ─── Client Review functions ───
  const fetchClientReviewJobs = async () => {
    try {
      setLoading(true);
      const jobsData = await api.getInstallerProjects({ status: 'client_review' });
      const transformed = jobsData.map(j => ({
        id: j.id,
        projectId: j.projectId || j.id,
        clientName: j.clientName,
        oemName: j.oemName,
        siteAddress: j.siteAddress || 'N/A',
        cityName: j.cityName,
        stateName: j.stateName,
        sqft: j.sqft,
        status: j.status,
        stageKey: j.stageKey,
        supervisorName: j.supervisorName,
        supervisorPhone: j.supervisorPhone,
        team: j.teamName,
      }));
      setCrJobs(transformed);
    } catch (error) {
      console.error('Error fetching client review jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientReviewChecklist = async (projectId) => {
    try {
      setCrChecklistLoading(true);
      setCrChecklist(null);
      const data = await api.getInstallerClientReview(projectId);
      setCrChecklist(data);
    } catch (error) {
      console.error('Error fetching client review checklist:', error);
      toast({ title: 'Error', description: 'Failed to load client review checklist', variant: 'destructive' });
    } finally {
      setCrChecklistLoading(false);
    }
  };

  const handleSelectCrJob = (job) => {
    setCrSelectedJob(job);
    setCrLocalChanges({});
    setCrItemPhotos({});
    setCrPhotosExpanded({});
    fetchClientReviewChecklist(job.projectId || job.id);
  };

  const handleCrLocalToggle = async (responseId) => {
    if (!crSelectedJob || !crChecklist) return;
    const projectId = crSelectedJob.projectId || crSelectedJob.id;
    const serverItem = crChecklist?.groups?.flatMap(g => g.items).find(i => i.id === responseId);
    if (!serverItem) return;
    // Lock only when supervisor has approved. Auto-approved items remain unchecking-allowed.
    if (serverItem.requiresSupervisorApproval && serverItem.isSupervisorApproved === true) return;

    const existing = crLocalChanges[responseId];
    const currentCompleted = existing !== undefined ? existing.isCompleted : serverItem.isCompleted;
    const newCompleted = !currentCompleted;
    const notes = existing?.notes ?? serverItem.notes ?? null;

    // Photo requirement gate (avoid pointless server round-trip)
    if (newCompleted && serverItem.isPhotoRequired && (serverItem.photoCount ?? 0) === 0) {
      toast({ title: 'Photo required', description: `Please upload a photo for '${serverItem.itemName}' first.`, variant: 'destructive' });
      return;
    }

    // Optimistic update — no refetch / no flicker.
    const willAutoApprove = newCompleted && !serverItem.requiresSupervisorApproval;
    const completedDelta = (willAutoApprove ? 1 : 0) - (serverItem.isApproved === true ? 1 : 0);

    const patchItem = (i) => i.id === responseId
      ? {
        ...i,
        isCompleted: newCompleted,
        isApproved: !newCompleted ? null : (serverItem.requiresSupervisorApproval ? false : true),
        isSupervisorApproved: !newCompleted ? null : i.isSupervisorApproved,
      }
      : i;

    const prevChecklist = crChecklist;
    setCrChecklist({
      ...crChecklist,
      completedItems: Math.max(0, (crChecklist.completedItems ?? 0) + completedDelta),
      groups: crChecklist.groups.map(g => ({ ...g, items: g.items.map(patchItem) })),
    });
    // Drop any local entry for this item (its toggle is now persisted server-side).
    setCrLocalChanges(prev => {
      if (prev[responseId] === undefined) return prev;
      const next = { ...prev };
      delete next[responseId];
      return next;
    });

    try {
      await api.updateClientReviewItem(projectId, responseId, newCompleted, notes);
    } catch (error) {
      // Revert on failure
      setCrChecklist(prevChecklist);
      toast({ title: 'Error', description: error.message || 'Failed to update item', variant: 'destructive' });
    }
  };

  const handleCrNotesChange = (responseId, value) => {
    setCrLocalChanges(prev => {
      const serverItem = crChecklist?.groups?.flatMap(g => g.items).find(i => i.id === responseId);
      const existing = prev[responseId];
      const currentCompleted = existing !== undefined ? existing.isCompleted : (serverItem?.isCompleted ?? false);
      return { ...prev, [responseId]: { isCompleted: currentCompleted, notes: value } };
    });
  };

  const handleCrSave = async () => {
    if (!crSelectedJob || Object.keys(crLocalChanges).length === 0) return;
    const projectId = crSelectedJob.projectId || crSelectedJob.id;
    setCrSaving(true);
    try {
      // Local changes only contain note edits now (toggles auto-save).
      const items = Object.entries(crLocalChanges).map(([responseId, change]) => ({
        responseId,
        isCompleted: change.isCompleted,
        notes: change.notes || null,
      }));
      const result = await api.batchUpdateClientReviewItems(projectId, items);
      if (result?.skipped > 0 && result?.updated === 0) {
        toast({ title: 'No changes saved', description: 'All edited items are locked (supervisor approved).', variant: 'destructive' });
      } else if (result?.skipped > 0) {
        toast({ title: 'Saved with skips', description: `Saved ${result.updated}, skipped ${result.skipped} (supervisor approved).` });
      } else {
        toast({ title: 'Saved', description: 'Notes saved' });
      }
      setCrLocalChanges({});
      await fetchClientReviewChecklist(projectId);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to save notes', variant: 'destructive' });
    } finally {
      setCrSaving(false);
    }
  };

  const handleCrPhotoUpload = async (projectId, responseId, file) => {
    if (!file) return;
    setCrUploadingPhoto(responseId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentTypeCode', DocumentTypeCode.SignoffPhoto);
      formData.append('referenceType', DocumentReferenceType.ClientReviewResponse);
      formData.append('referenceId', responseId);

      const token = localStorage.getItem('alignworks_token');
      const response = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');
      toast({ title: 'Photo Uploaded', description: 'Photo uploaded successfully' });
      setCrItemPhotos(prev => { const n = { ...prev }; delete n[responseId]; return n; });
      await fetchClientReviewChecklist(projectId);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({ title: 'Error', description: 'Failed to upload photo', variant: 'destructive' });
    } finally {
      setCrUploadingPhoto(null);
    }
  };

  const loadCrItemPhotos = async (projectId, responseId) => {
    if (crItemPhotos[responseId]) {
      setCrPhotosExpanded(prev => ({ ...prev, [responseId]: !prev[responseId] }));
      return;
    }
    try {
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const docsRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents?referenceId=${responseId}`, { headers });
      if (!docsRes.ok) throw new Error('Failed to fetch documents');
      const data = await docsRes.json();
      const docsList = data.documents || data || [];

      const photos = [];
      for (const doc of docsList) {
        const streamRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${doc.id}/stream`, { headers });
        if (streamRes.ok) {
          const blob = await streamRes.blob();
          photos.push({ id: doc.id, url: URL.createObjectURL(blob), fileName: doc.fileName });
        }
      }
      setCrItemPhotos(prev => ({ ...prev, [responseId]: photos }));
      setCrPhotosExpanded(prev => ({ ...prev, [responseId]: true }));
    } catch (error) {
      console.error('Error loading photos:', error);
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    }
  };

  const handleCrDeletePhoto = async (projectId, documentId, responseId) => {
    try {
      const token = localStorage.getItem('alignworks_token');
      const response = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Delete failed');
      setCrItemPhotos(prev => ({
        ...prev,
        [responseId]: prev[responseId]?.filter(p => p.id !== documentId) || []
      }));
      toast({ title: 'Photo Deleted' });
      await fetchClientReviewChecklist(projectId);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete photo', variant: 'destructive' });
    }
  };

  const handleCompleteClientReview = async () => {
    if (!crSelectedJob) return;
    const projectId = crSelectedJob.projectId || crSelectedJob.id;
    setCrCompleting(true);
    try {
      await api.completeClientReview(projectId);
      toast({ title: 'Success', description: 'Client review completed!' });
      setCrSelectedJob(null);
      setCrChecklist(null);
      fetchClientReviewJobs();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to complete client review', variant: 'destructive' });
    } finally {
      setCrCompleting(false);
    }
  };

  // Find any active check-in today (clocked in but not yet clocked out)
  const getAnyActiveCheckIn = () => {
    const today = new Date().toDateString();
    return currentMonthAttendance.find(r => !r.checkOutTime && new Date(r.checkInTime).toDateString() === today) || null;
  };
  const anyActiveCheckIn = getAnyActiveCheckIn();
  const isClockOut = !!anyActiveCheckIn;

  const handleGetLocation = (overrideProjectId) => {
    const pid = overrideProjectId || attendanceProjectId;
    if (!pid) {
      toast({ title: 'Select Project First', description: 'Please select a project before capturing GPS', variant: 'destructive' });
      return;
    }
    // Search across all available job lists
    const project = jobs.find(j => j.projectId === pid)
      || completedJobs.find(j => j.projectId === pid)
      || crJobs.find(j => j.projectId === pid);
    if (!project?.latitude || !project?.longitude) {
      toast({ title: 'No Site Location', description: 'This project has no GPS coordinates set. Please contact your supervisor to update the project location.', variant: 'destructive' });
      return;
    }
    setCapturingGps(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const dist = calcDistanceMeters(
            position.coords.latitude, position.coords.longitude,
            project.latitude, project.longitude
          );
          const maxRadius = project.locationValidationRadius ?? 50;
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            distance: dist,
            maxRadius,
            timestamp: new Date().toISOString()
          });
          setCapturingGps(false);
        },
        () => {
          toast({ title: 'GPS Error', description: 'Could not get your location. Please enable GPS.', variant: 'destructive' });
          setCapturingGps(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      toast({ title: 'GPS Not Supported', description: 'Your device does not support GPS', variant: 'destructive' });
      setCapturingGps(false);
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } });
      cameraStreamRef.current = stream;
      setCameraActive(true);
      // Attach stream to video element after state update
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.name === 'NotAllowedError' ? 'Camera permission denied. Please allow camera access.' : 'Could not access camera. Please check permissions.');
      toast({ title: 'Camera Error', description: 'Could not access camera', variant: 'destructive' });
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelfieFile(file);
        setSelfiePreview(canvas.toDataURL('image/jpeg'));
        toast({ title: 'Selfie Captured' });
      }
    }, 'image/jpeg', 0.85);
    stopCamera();
  };

  const handleMarkAttendance = async () => {
    if (!attendanceProjectId) {
      toast({ title: 'Required', description: 'Please select a project', variant: 'destructive' });
      return;
    }
    if (!gpsLocation) {
      toast({ title: 'Required', description: 'Please capture GPS location', variant: 'destructive' });
      return;
    }
    if (!selfieFile) {
      toast({ title: 'Required', description: 'Please capture selfie', variant: 'destructive' });
      return;
    }
    setSubmittingAttendance(true);
    try {
      const result = await api.markAttendance(
        attendanceProjectId,
        gpsLocation.latitude,
        gpsLocation.longitude,
        gpsLocation.accuracy,
        selfieFile
      );
      toast({
        title: 'Clocked In',
        description: result.message || `Checked in (${result.distance >= 1000 ? `${(result.distance / 1000).toFixed(1)}km` : `${result.distance?.toFixed(0) || 0}m`} from site)`
      });
      setAttendanceDialogOpen(false);
      stopCamera();
      setSelfiePreview(null);
      setSelfieFile(null);
      setGpsLocation(null);
      setAttendanceProjectId('');
      fetchData();
      fetchMyAttendance(new Date()); // always refresh current month
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to mark attendance',
        variant: 'destructive'
      });
    } finally {
      setSubmittingAttendance(false);
    }
  };

  const handleClockOut = async () => {
    if (!anyActiveCheckIn) return;
    if (!gpsLocation) {
      toast({ title: 'Required', description: 'Please capture GPS location', variant: 'destructive' });
      return;
    }
    if (!selfieFile) {
      toast({ title: 'Required', description: 'Please capture selfie', variant: 'destructive' });
      return;
    }
    setSubmittingAttendance(true);
    try {
      const result = await api.checkOutAttendance(
        anyActiveCheckIn.id,
        gpsLocation.latitude,
        gpsLocation.longitude,
        gpsLocation.accuracy,
        selfieFile
      );
      toast({
        title: 'Clocked Out',
        description: result.message || 'Successfully clocked out'
      });
      setAttendanceDialogOpen(false);
      stopCamera();
      setSelfiePreview(null);
      setSelfieFile(null);
      setGpsLocation(null);
      setAttendanceProjectId('');
      fetchData();
      fetchMyAttendance(new Date()); // always refresh current month
    } catch (error) {
      console.error('Error clocking out:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to clock out',
        variant: 'destructive'
      });
    } finally {
      setSubmittingAttendance(false);
    }
  };

  const fetchMyAttendance = async (targetDate) => {
    setLoadingAttendance(true);
    try {
      const d = targetDate || attendanceMonth;
      const records = await api.getMyAttendance(d.getMonth() + 1, d.getFullYear());
      // Resolve selfie streams (check-in and check-out)
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const resolved = await Promise.all((records || []).map(async (record) => {
        let selfieObjectUrl = null;
        let checkOutSelfieObjectUrl = null;
        try {
          const res = await fetch(`${api.baseUrl}/api/installer/attendance/${record.id}/selfie`, { headers });
          if (res.ok) {
            const blob = await res.blob();
            selfieObjectUrl = URL.createObjectURL(blob);
          }
        } catch (e) { /* skip */ }
        if (record.checkOutSelfieUrl) {
          try {
            const res = await fetch(`${api.baseUrl}/api/installer/attendance/${record.id}/selfie?type=checkout`, { headers });
            if (res.ok) {
              const blob = await res.blob();
              checkOutSelfieObjectUrl = URL.createObjectURL(blob);
            }
          } catch (e) { /* skip */ }
        }
        return { ...record, selfieObjectUrl, checkOutSelfieObjectUrl };
      }));
      setMyAttendance(resolved);
      // If loading current month, also update the card/clock-in data
      const now = new Date();
      if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
        setCurrentMonthAttendance(resolved);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleUpdateProfile = () => {
    updateProfile(profileData);
    toast({
      title: 'Profile Updated',
      description: 'Your profile has been updated successfully'
    });
    setProfileDialogOpen(false);
  };

  // Check if current time is within a progress report time window (IST)
  const isWithinTimeWindow = () => {
    const now = new Date();
    // Convert to IST (UTC+5:30)
    const istOffset = 5.5 * 60; // minutes
    const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
    const istMinutes = (utcMinutes + istOffset) % 1440;
    // Windows: 1:00-1:30 PM (780-810), 4:00-4:30 PM (960-990), 7:00-7:30 PM (1140-1170)
    return (istMinutes >= 780 && istMinutes <= 810) ||
      (istMinutes >= 960 && istMinutes <= 990) ||
      (istMinutes >= 1140 && istMinutes <= 1170);
  };

  const getCurrentTimeWindow = () => {
    const now = new Date();
    const istOffset = 5.5 * 60;
    const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
    const istMinutes = (utcMinutes + istOffset) % 1440;
    if (istMinutes >= 780 && istMinutes <= 810) return '1:00 PM - 1:30 PM';
    if (istMinutes >= 960 && istMinutes <= 990) return '4:00 PM - 4:30 PM';
    if (istMinutes >= 1140 && istMinutes <= 1170) return '7:00 PM - 7:30 PM';
    return null;
  };

  const getNextTimeWindow = () => {
    const now = new Date();
    const istOffset = 5.5 * 60;
    const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
    const istMinutes = (utcMinutes + istOffset) % 1440;
    if (istMinutes < 780) return '1:00 PM';
    if (istMinutes > 810 && istMinutes < 960) return '4:00 PM';
    if (istMinutes > 990 && istMinutes < 1140) return '7:00 PM';
    if (istMinutes > 1170) return '1:00 PM (tomorrow)';
    return null;
  };

  // Check if any active project has override enabled
  const hasOverrideProject = () => {
    return jobs.some(j => j.allowProgressReportAnytime);
  };

  const canReportProgress = () => {
    return isWithinTimeWindow() || hasOverrideProject();
  };

  const handleOpenProgressDialog = () => {
    setProgressDialogOpen(true);
    setProgressProjectId('');
    setProgressDescription('');
    setProgressIssues('');
    setProgressGps(null);
    setProgressPhotos([]);
  };

  const captureProgressGps = (overrideProjectId) => {
    const pid = overrideProjectId || progressProjectId;
    if (!pid) {
      toast({ title: 'Select Project First', description: 'Please select a project before capturing GPS', variant: 'destructive' });
      return;
    }
    const project = jobs.find(j => j.projectId === pid);
    setCapturingProgressGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        let distance = null;
        let maxRadius = null;
        if (project?.latitude && project?.longitude) {
          distance = calcDistanceMeters(pos.coords.latitude, pos.coords.longitude, project.latitude, project.longitude);
          maxRadius = project.locationValidationRadius ?? 50;
        }
        setProgressGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, distance, maxRadius });
        setCapturingProgressGps(false);
      },
      () => {
        toast({ title: 'GPS Error', description: 'Could not capture location. Please enable GPS.', variant: 'destructive' });
        setCapturingProgressGps(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleProgressPhotoCapture = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setProgressPhotos(prev => [...prev, { file, preview: ev.target.result }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeProgressPhoto = (index) => {
    setProgressPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitProgressReport = async () => {
    if (!progressProjectId) {
      toast({ title: 'Required', description: 'Please select a project', variant: 'destructive' });
      return;
    }
    if (!progressGps) {
      toast({ title: 'Required', description: 'Please capture GPS location', variant: 'destructive' });
      return;
    }
    if (progressPhotos.length === 0) {
      toast({ title: 'Required', description: 'Please capture at least one photo', variant: 'destructive' });
      return;
    }

    setSubmittingProgress(true);
    try {
      // First upload photos
      const photoUrls = [];
      const token = localStorage.getItem('alignworks_token');
      for (const photo of progressPhotos) {
        const formData = new FormData();
        formData.append('file', photo.file);
        formData.append('documentTypeCode', DocumentTypeCode.ProgressReport);
        formData.append('referenceType', DocumentReferenceType.ProgressReport);

        const response = await fetch(`${api.baseUrl}/api/projects/${progressProjectId}/documents/upload`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
        });

        if (!response.ok) throw new Error('Photo upload failed');
        const result = await response.json();
        if (result?.blobUrl || result?.url || result?.fileUrl) {
          photoUrls.push(result.blobUrl || result.url || result.fileUrl);
        }
      }

      // Then submit report
      await api.submitProgressReport({
        projectId: progressProjectId,
        latitude: progressGps.lat,
        longitude: progressGps.lng,
        workDescription: progressDescription,
        issues: progressIssues || null,
        photos: photoUrls
      });

      toast({ title: 'Success', description: 'Progress report submitted successfully' });
      setProgressDialogOpen(false);
    } catch (error) {
      const msg = error?.message || 'Failed to submit progress report';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSubmittingProgress(false);
    }
  };

  const fetchMyProgressReports = async () => {
    setLoadingMyReports(true);
    try {
      const reports = await api.getMyProgressReports();
      // Resolve photo streams into object URLs
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const resolved = await Promise.all((reports || []).map(async (report) => {
        const photoUrls = [];
        for (const doc of (report.photoDocuments || [])) {
          try {
            const res = await fetch(`${api.baseUrl}/api/projects/${report.projectId}/documents/${doc.id}/stream`, { headers });
            if (res.ok) {
              const blob = await res.blob();
              photoUrls.push(URL.createObjectURL(blob));
            }
          } catch (e) { /* skip */ }
        }
        return { ...report, resolvedPhotos: photoUrls };
      }));
      setMyProgressReports(resolved);
    } catch (error) {
      console.error('Error fetching progress reports:', error);
    } finally {
      setLoadingMyReports(false);
    }
  };


  const calculateIncentive = (job) => {
    if (job.incentive !== undefined) {
      let status = 'On Time';
      let rate = job.incentiveRate || 15;
      if (rate === 25) status = 'Before Time';
      else if (rate === 10) status = 'After Time';
      return { amount: job.incentive, rate, status, sqft: job.sqft };
    }

    if (!job.deliveryDate || !job.completedDate) {
      return { amount: 0, rate: 0, status: 'N/A', sqft: job.sqft };
    }

    const deliveryDate = parseISO(job.deliveryDate);
    const completedDate = parseISO(job.completedDate);
    const daysDiff = differenceInDays(completedDate, deliveryDate);

    let rate = 15;
    let status = 'On Time';
    if (daysDiff < 0) {
      rate = 25;
      status = 'Before Time';
    } else if (daysDiff > 0) {
      rate = 10;
      status = 'After Time';
    }

    return { amount: job.sqft * rate, rate, status, sqft: job.sqft };
  };



  const handleChecklistToggle = async (responseId) => {
    if (!selectedJob || !checklistData) return;
    const projectId = selectedJob.projectId || selectedJob.id;

    // Find the item in checklistData
    let foundItem = null;
    for (const entity of checklistData.entities) {
      for (const unit of entity.units) {
        for (const group of unit.groups) {
          const item = group.items.find(i => i.id === responseId);
          if (item) { foundItem = item; break; }
        }
        if (foundItem) break;
      }
      if (foundItem) break;
    }

    if (!foundItem) return;

    // Approval lock: only supervisor-approved items are locked. Auto-approved items
    // (no supervisor required) can still be unchecked if the installer misclicked.
    if (foundItem.requiresSupervisorApproval && foundItem.isSupervisorApproved === true) {
      toast({
        title: 'Locked',
        description: 'This item has been approved by the supervisor and cannot be modified',
        variant: 'destructive'
      });
      return;
    }

    if (!foundItem.isCompleted && foundItem.requiresPhoto && foundItem.photoCount === 0) {
      toast({
        title: 'Photo Required',
        description: 'Please upload photos before marking this item as completed',
        variant: 'destructive'
      });
      return;
    }

    const newCompletedState = !foundItem.isCompleted;

    // Optimistic update on checklistData
    // completedItems = truly done (isApproved=true)
    // Items without supervisor requirement auto-approve, so count them
    const willAutoApprove = newCompletedState && !foundItem.requiresSupervisorApproval;
    const wasAutoApproved = !newCompletedState && foundItem.isApproved === true;
    const completedDelta = willAutoApprove ? 1 : wasAutoApproved ? -1 : 0;

    const updateEntities = (entities) => entities.map(e => ({
      ...e,
      units: e.units.map(u => ({
        ...u,
        completedItems: u.completedItems + completedDelta,
        groups: u.groups.map(g => ({
          ...g,
          items: g.items.map(i => i.id === responseId ? { ...i, isCompleted: newCompletedState, isApproved: willAutoApprove ? true : !newCompletedState ? null : false } : i)
        }))
      }))
    }));

    const prevChecklistData = checklistData;
    setChecklistData({
      ...checklistData,
      completedItems: checklistData.completedItems + completedDelta,
      entities: updateEntities(checklistData.entities)
    });

    // Update left panel totals too
    setJobs(jobs.map(j => j.id === selectedJob.id ? {
      ...j,
      completedItems: j.completedItems + completedDelta
    } : j));
    setSelectedJob(prev => prev ? { ...prev, completedItems: prev.completedItems + completedDelta } : prev);

    try {
      await api.updateChecklistItem(projectId, responseId, newCompletedState, foundItem.notes);
    } catch (error) {
      console.error('Error updating checklist:', error);
      // Revert
      setChecklistData(prevChecklistData);
      toast({ title: 'Error', description: error.message || 'Failed to update checklist item', variant: 'destructive' });
      refreshChecklist();
    }
  };

  // Notes update (local state only, saved via Save Progress)
  const handleNotesChange = (responseId, notes) => {
    if (!checklistData) return;
    setDirtyNoteIds(prev => {
      const next = new Set(prev);
      next.add(responseId);
      return next;
    });
    setChecklistData(prev => ({
      ...prev,
      entities: prev.entities.map(e => ({
        ...e,
        units: e.units.map(u => ({
          ...u,
          groups: u.groups.map(g => ({
            ...g,
            items: g.items.map(i => i.id === responseId ? { ...i, notes } : i)
          }))
        }))
      }))
    }));
  };

  // Photo management
  const handlePhotoUpload = async (projectId, responseId, file) => {
    if (!file) return;
    setUploadingPhoto(responseId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentTypeCode', DocumentTypeCode.InstallationPhoto);
      formData.append('referenceType', DocumentReferenceType.InstallationChecklistResponse);
      formData.append('referenceId', responseId);

      const token = localStorage.getItem('alignworks_token');
      const response = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');

      toast({ title: 'Photo Uploaded', description: 'Photo has been uploaded successfully' });
      // Clear cached photos for this item so they reload fresh
      setItemPhotos(prev => { const n = { ...prev }; delete n[responseId]; return n; });
      refreshChecklist();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({ title: 'Error', description: 'Failed to upload photo', variant: 'destructive' });
    } finally {
      setUploadingPhoto(null);
    }
  };

  const loadItemPhotos = async (projectId, responseId) => {
    if (itemPhotos[responseId]) {
      setPhotosExpanded(prev => ({ ...prev, [responseId]: !prev[responseId] }));
      return;
    }
    try {
      const token = localStorage.getItem('alignworks_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const docsRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents?referenceId=${responseId}`, { headers });
      if (!docsRes.ok) throw new Error('Failed to fetch documents');
      const data = await docsRes.json();
      const docsList = data.documents || data || [];

      const photos = [];
      for (const doc of docsList) {
        const streamRes = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${doc.id}/stream`, { headers });
        if (streamRes.ok) {
          const blob = await streamRes.blob();
          photos.push({ id: doc.id, url: URL.createObjectURL(blob), fileName: doc.fileName });
        }
      }
      setItemPhotos(prev => ({ ...prev, [responseId]: photos }));
      setPhotosExpanded(prev => ({ ...prev, [responseId]: true }));
    } catch (error) {
      console.error('Error loading photos:', error);
      toast({ title: 'Error', description: 'Failed to load photos', variant: 'destructive' });
    }
  };

  const handleDeletePhoto = async (projectId, documentId, responseId) => {
    try {
      const token = localStorage.getItem('alignworks_token');
      const response = await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Delete failed');

      setItemPhotos(prev => ({
        ...prev,
        [responseId]: prev[responseId]?.filter(p => p.id !== documentId) || []
      }));
      toast({ title: 'Photo Deleted' });
      refreshChecklist();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast({ title: 'Error', description: 'Failed to delete photo', variant: 'destructive' });
    }
  };

  const handleSaveProgress = async () => {
    if (!selectedJob || !checklistData) return;
    const projectId = selectedJob.projectId || selectedJob.id;

    // Collect only items whose notes were edited locally and are still mutable.
    const allItems = [];
    checklistData.entities.forEach(e => e.units.forEach(u => u.groups.forEach(g => g.items.forEach(i => allItems.push(i)))));
    const dirtyItems = allItems.filter(i => dirtyNoteIds.has(i.id) && !(i.requiresSupervisorApproval && i.isSupervisorApproved === true));

    if (dirtyItems.length === 0) {
      toast({
        title: 'All saved',
        description: 'Your checks are saved automatically. Only edited notes need this button.',
      });
      return;
    }

    setSavingProgress(true);
    try {
      const payload = dirtyItems.map(i => ({
        responseId: i.id,
        isCompleted: !!i.isCompleted,
        notes: i.notes ?? null,
      }));
      await api.batchUpdateChecklistItems(projectId, payload);
      setDirtyNoteIds(new Set());
      toast({ title: 'Progress Saved', description: `Saved ${dirtyItems.length} item(s).` });
      refreshChecklist();
    } catch (error) {
      console.error('Error saving progress:', error);
      toast({ title: 'Error', description: 'Failed to save progress', variant: 'destructive' });
    } finally {
      setSavingProgress(false);
    }
  };

  const handleCompleteJob = async () => {
    if (!selectedJob) return;
    const projectId = selectedJob.projectId || selectedJob.id;

    setCompletingJob(true);
    try {
      await api.completeInstallation(projectId);
      toast({
        title: 'Job Completed',
        description: 'Installation has been marked as completed.',
      });
      // Clear the open job so the right pane resets, then refresh list.
      setSelectedJob(null);
      setChecklistData(null);
      setDirtyNoteIds(new Set());
      await fetchData();
    } catch (error) {
      console.error('Error completing job:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete job',
        variant: 'destructive'
      });
    } finally {
      setCompletingJob(false);
    }
  };

  const getJobStatusColor = (status) => {
    const colors = {
      'Scheduled': 'text-blue-600 bg-blue-50',
      'In Progress': 'text-purple-600 bg-purple-50',
      'Completed': 'text-green-600 bg-green-50',
      'Client Review': 'text-cyan-600 bg-cyan-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  const getProgress = (job) => {
    if (!job || !job.totalItems || job.totalItems === 0) return 0;
    return (job.completedItems / job.totalItems) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Aligner Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your installations and track progress</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/employee/profile')}
            className="border-gray-300 self-start sm:self-auto"
          >
            <User className="w-4 h-4 mr-1" />
            Profile
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card
            className={`cursor-pointer hover:shadow-lg transition-all border-2 ${viewMode === 'jobs' && activeTab === 'active' ? 'border-purple-500 ring-1 ring-purple-200' : 'border-transparent'}`}
            onClick={() => {
              setViewMode('jobs');
              if (activeTab !== 'active') {
                setActiveTab('active');
                setSelectedJob(null);
                setChecklistData(null);
                fetchData();
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Jobs at Hand</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">{jobs.length}</p>
                </div>
                <Briefcase className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer hover:shadow-lg transition-all border-2 ${viewMode === 'jobs' && activeTab === 'completed' ? 'border-green-500 ring-1 ring-green-200' : 'border-transparent'}`}
            onClick={() => {
              setViewMode('jobs');
              if (activeTab !== 'completed') {
                setActiveTab('completed');
                setSelectedJob(null);
                setChecklistData(null);
                fetchCompletedJobs();
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed Jobs</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{completedJobs.length}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer hover:shadow-lg transition-all border-2 ${viewMode === 'jobs' && activeTab === 'client_review' ? 'border-cyan-500 ring-1 ring-cyan-200' : 'border-transparent'}`}
            onClick={() => {
              setViewMode('jobs');
              if (activeTab !== 'client_review') {
                setActiveTab('client_review');
                setSelectedJob(null);
                setChecklistData(null);
                setCrSelectedJob(null);
                setCrChecklist(null);
                fetchClientReviewJobs();
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Client Review</p>
                  <p className="text-3xl font-bold text-cyan-600 mt-1">{crJobs.length}</p>
                </div>
                <ClipboardCheck className="w-8 h-8 text-cyan-600" />
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer hover:shadow-lg transition-all border-2 ${viewMode === 'reports' ? 'border-blue-500 ring-1 ring-blue-200' : 'border-transparent'}`}
            onClick={() => {
              setViewMode('reports');
              if (myProgressReports.length === 0) fetchMyProgressReports();
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Report Progress</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{myProgressReports.length}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer hover:shadow-lg transition-all border-2 ${viewMode === 'attendance' ? 'border-orange-500 ring-1 ring-orange-200' : 'border-transparent'}`}
            onClick={() => {
              setViewMode('attendance');
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Attendance</p>
                  <p className="text-3xl font-bold text-orange-600 mt-1">
                    {(() => {
                      const uniqueDays = new Set(currentMonthAttendance.map(r => new Date(r.checkInTime).toDateString()));
                      return uniqueDays.size;
                    })()}
                    <span className="text-sm font-normal text-gray-500 ml-1">days</span>
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">
                      {(() => {
                        const totalHrs = currentMonthAttendance.filter(r => r.hoursWorked != null).reduce((sum, r) => sum + r.hoursWorked, 0);
                        const h = Math.floor(totalHrs);
                        const m = Math.round((totalHrs - h) * 60);
                        return `${h}h ${m}m worked`;
                      })()}
                    </span>
                    {(() => {
                      const today = new Date().toDateString();
                      const missed = currentMonthAttendance.filter(r => !r.checkOutTime && new Date(r.checkInTime).toDateString() !== today).length;
                      return missed > 0 ? (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{missed} missed</span>
                      ) : null;
                    })()}
                  </div>
                </div>
                <CalendarCheck className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <Button
            className={isClockOut ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}
            onClick={() => {
              setGpsLocation(null);
              setSelfiePreview(null);
              setSelfieFile(null);
              if (isClockOut && anyActiveCheckIn) {
                setAttendanceProjectId(anyActiveCheckIn.projectId);
                setAttendanceDialogOpen(true);
                setTimeout(() => handleGetLocation(anyActiveCheckIn.projectId), 300);
              } else {
                setAttendanceProjectId('');
                setAttendanceDialogOpen(true);
              }
            }}
          >
            <MapPinned className="w-4 h-4 mr-2" />
            {isClockOut ? 'Clock Out' : 'Clock In'}
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleOpenProgressDialog}
            disabled={!canReportProgress()}
          >
            <Clock className="w-4 h-4 mr-2" />
            Report Progress
          </Button>
          {!canReportProgress() && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
              🕐 Next window opens at {getNextTimeWindow()} IST
            </span>
          )}
        </div>

        {/* Progress Reports View */}
        {viewMode === 'reports' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">My Progress Reports</CardTitle>
              <CardDescription>All progress reports you have submitted</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMyReports ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
              ) : myProgressReports.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No progress reports submitted yet.</div>
              ) : (
                <div className="space-y-4">
                  {myProgressReports.map((report) => (
                    <div key={report.id} className="border rounded-lg p-4 space-y-2 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{report.updatedByName || 'Unknown'}</span>
                          {report.teamName && report.teamName !== 'N/A' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{report.teamName}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {report.isOverride && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Override</span>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            {report.timeWindow || 'N/A'}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">{new Date(report.updateDate).toLocaleString()}{report.projectName ? ` — ${report.projectName}` : ''}</p>
                      {report.workDescription && <p className="text-sm text-gray-700">{report.workDescription}</p>}
                      {report.issues && <p className="text-sm text-red-600">Issues: {report.issues}</p>}
                      <p className="text-xs text-gray-400">📍 {report.latitude?.toFixed(5)}, {report.longitude?.toFixed(5)}</p>
                      {report.resolvedPhotos?.length > 0 && (
                        <div className="flex gap-2 flex-wrap mt-1">
                          {report.resolvedPhotos.map((url, idx) => (
                            <img
                              key={idx}
                              src={url}
                              alt={`Photo ${idx + 1}`}
                              className="w-16 h-16 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => setEnlargedPhoto({ url, fileName: `Report photo ${idx + 1}` })}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Attendance View */}
        {viewMode === 'attendance' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">My Attendance — {format(attendanceMonth, 'MMMM yyyy')}</CardTitle>
                  <CardDescription>Your clock-in / clock-out records for {format(attendanceMonth, 'MMMM yyyy')}</CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => {
                    const prev = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth() - 1, 1);
                    setAttendanceMonth(prev);
                    fetchMyAttendance(prev);
                  }}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {(attendanceMonth.getMonth() !== new Date().getMonth() || attendanceMonth.getFullYear() !== new Date().getFullYear()) && (
                    <Button size="sm" variant="outline" onClick={() => {
                      const now = new Date();
                      const cur = new Date(now.getFullYear(), now.getMonth(), 1);
                      setAttendanceMonth(cur);
                      fetchMyAttendance(cur);
                    }}>
                      Current
                    </Button>
                  )}
                  <Button size="sm" variant="outline" disabled={
                    attendanceMonth.getMonth() === new Date().getMonth() && attendanceMonth.getFullYear() === new Date().getFullYear()
                  } onClick={() => {
                    const next = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth() + 1, 1);
                    setAttendanceMonth(next);
                    fetchMyAttendance(next);
                  }}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingAttendance ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div>
              ) : myAttendance.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No attendance records yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1000px]">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Date</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Project</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Clock In</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600 whitespace-nowrap">In Selfie</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600 whitespace-nowrap">In Distance</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Clock Out</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Out Selfie</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Out Distance</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600 whitespace-nowrap">Hours Worked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myAttendance.map((record) => {
                        const checkIn = record.checkInTime ? new Date(record.checkInTime) : null;
                        const checkOut = record.checkOutTime ? new Date(record.checkOutTime) : null;
                        const hoursWorked = record.hoursWorked != null ? record.hoursWorked : null;
                        const hrs = hoursWorked != null ? Math.floor(hoursWorked) : null;
                        const mins = hoursWorked != null ? Math.round((hoursWorked - hrs) * 60) : null;

                        return (
                          <tr key={record.id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="py-2 px-3 whitespace-nowrap">
                              {checkIn ? format(checkIn, 'dd MMM yyyy') : '—'}
                            </td>
                            <td className="py-2 px-3">
                              <span className="font-medium text-gray-900">{record.projectName}</span>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              {checkIn ? format(checkIn, 'hh:mm a') : '—'}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {record.selfieObjectUrl ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800"
                                  onClick={() => setEnlargedPhoto({ url: record.selfieObjectUrl, fileName: 'Clock-In Selfie' })}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                                </Button>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center whitespace-nowrap">
                              {record.distanceFromSite != null ? (
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${record.locationStatus === 'verified' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {Number(record.distanceFromSite).toFixed(0)}m
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              {checkOut ? (
                                <span className="text-green-700">{format(checkOut, 'hh:mm a')}</span>
                              ) : (
                                <span className="text-amber-600 text-xs font-medium">Pending</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {record.checkOutSelfieObjectUrl ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800"
                                  onClick={() => setEnlargedPhoto({ url: record.checkOutSelfieObjectUrl, fileName: 'Clock-Out Selfie' })}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                                </Button>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center whitespace-nowrap">
                              {record.checkOutDistanceFromSite != null ? (
                                <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                  {Number(record.checkOutDistanceFromSite).toFixed(0)}m
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              {hoursWorked != null ? (
                                <span className="font-medium text-gray-900">{hrs}h {mins}m</span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Job Cards */}
        {viewMode === 'jobs' && activeTab !== 'client_review' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Job List */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {activeTab === 'active' ? 'Jobs at Hand' : 'Completed Jobs'}
            </h2>

            {/* Job List */}
            {(activeTab === 'active' ? jobs : completedJobs).length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-gray-500">
                  {activeTab === 'active' ? 'No active jobs' : 'No completed jobs'}
                </CardContent>
              </Card>
            ) : (
              (activeTab === 'active' ? jobs : completedJobs).map(job => (
                <Card
                  key={job.id}
                  className={`cursor-pointer transition-all border-2 ${selectedJob?.id === job.id ? 'border-teal-500 bg-teal-50/50 shadow-md' : 'border-transparent hover:shadow-md hover:border-gray-200'}`}
                  onClick={() => handleSelectJob(job)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold">{job.clientName}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${getJobStatusColor(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span>{job.date} • {job.time}</span>
                    </div>
                    <Progress value={getProgress(job)} className="h-2" />
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                      <span>{job.sqft} sq.ft</span>
                      <span>{Math.round(getProgress(job))}% complete</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Job Details */}
          <div className="lg:col-span-2">
            {selectedJob ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{selectedJob.clientName}</CardTitle>
                      <CardDescription>{selectedJob.oemName}</CardDescription>
                      {selectedJob.supervisorName && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Supervisor:</span> {selectedJob.supervisorName}
                          {selectedJob.supervisorPhone && (
                            <a
                              href={`tel:${selectedJob.supervisorPhone}`}
                              className="ml-2 text-teal-600 hover:underline"
                            >
                              {selectedJob.supervisorPhone}
                            </a>
                          )}
                        </p>
                      )}
                      {selectedJob.siteAddress && selectedJob.siteAddress !== 'N/A' && (
                        <p className="text-sm text-gray-500 mt-1">
                          <MapPin className="w-3.5 h-3.5 inline mr-1" />
                          {selectedJob.siteAddress}
                        </p>
                      )}
                    </div>
                    {(selectedJob.latitude || selectedJob.longitude || (selectedJob.siteAddress && selectedJob.siteAddress !== 'N/A')) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => {
                          const query = selectedJob.latitude && selectedJob.longitude
                            ? `${selectedJob.latitude},${selectedJob.longitude}`
                            : encodeURIComponent(selectedJob.siteAddress);
                          window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
                        }}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View Location
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <h3 className="font-semibold text-gray-900 mb-3">Installation Checklist</h3>
                  <div className="text-xs text-gray-500 mb-3">
                    {checklistData ? `${checklistData.completedItems}/${checklistData.totalItems}` : `${selectedJob.completedItems}/${selectedJob.totalItems}`} items completed
                  </div>

                  {/* Search */}
                  {checklistData?.entities?.length > 0 && (
                    <div className="relative mb-3">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search checklist items..."
                        value={checklistSearch}
                        onChange={(e) => setChecklistSearch(e.target.value)}
                        className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                      {checklistSearch && (
                        <button
                          type="button"
                          onClick={() => setChecklistSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {checklistLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                      <span className="ml-2 text-sm text-gray-500">Loading checklist...</span>
                    </div>
                  ) : checklistData?.entities?.length > 0 ? (
                    (() => {
                      const searchTerm = checklistSearch.trim().toLowerCase();
                      const filteredEntities = searchTerm
                        ? checklistData.entities.map(entity => ({
                          ...entity,
                          units: entity.units.map(unit => ({
                            ...unit,
                            groups: unit.groups
                              .map(g => ({ ...g, items: g.items.filter(it => (it.item || '').toLowerCase().includes(searchTerm)) }))
                              .filter(g => g.items.length > 0)
                          })).filter(unit => unit.groups.length > 0)
                        })).filter(entity => entity.units.length > 0)
                        : checklistData.entities;

                      if (searchTerm && filteredEntities.length === 0) {
                        return (
                          <div className="text-center py-8 text-sm text-gray-500">
                            No checklist items match "{checklistSearch}"
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-3">
                          {filteredEntities.map((entity) => {
                            const entityKey = entity.entityId;
                            const isEntityExpanded = searchTerm ? true : expandedEntities[entityKey] === true;
                            const entityCompleted = entity.units.reduce((s, u) => s + u.completedItems, 0);
                            const entityTotal = entity.units.reduce((s, u) => s + u.totalItems, 0);

                            return (
                              <div key={entityKey} className="border rounded-lg overflow-hidden">
                                {/* Entity Header */}
                                <button
                                  className="w-full flex items-center justify-between p-3 bg-teal-50 hover:bg-teal-100 transition-colors text-left"
                                  onClick={() => setExpandedEntities(prev => ({ ...prev, [entityKey]: !isEntityExpanded }))}
                                >
                                  <div className="flex items-center gap-2">
                                    {isEntityExpanded ? <ChevronUp className="w-4 h-4 text-teal-600" /> : <ChevronDown className="w-4 h-4 text-teal-600" />}
                                    <span className="font-semibold text-teal-800">{entity.entityName}</span>
                                    <span className="text-xs text-teal-600">({entity.quantity} units)</span>
                                  </div>
                                  <span className="text-xs font-medium text-teal-700">{entityCompleted}/{entityTotal}</span>
                                </button>

                                {/* Entity Body */}
                                {isEntityExpanded && (
                                  <div className="p-2 space-y-2">
                                    {entity.units.map((unit) => {
                                      const unitKey = unit.unitId;
                                      const isUnitExpanded = searchTerm ? true : expandedUnits[unitKey] === true;
                                      const unitStatusColor = unit.status === 'completed' ? 'text-green-600 bg-green-50'
                                        : unit.status === 'in_progress' ? 'text-purple-600 bg-purple-50'
                                          : 'text-gray-600 bg-gray-50';

                                      return (
                                        <div key={unitKey} className="border rounded-lg overflow-hidden">
                                          {/* Unit Header */}
                                          <button
                                            className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                            onClick={() => setExpandedUnits(prev => ({ ...prev, [unitKey]: !isUnitExpanded }))}
                                          >
                                            <div className="flex items-center gap-2">
                                              {isUnitExpanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-500" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />}
                                              <span className="font-medium text-sm text-gray-800">
                                                {unit.unitName || `Unit ${unit.unitNumber}`}
                                              </span>
                                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${unitStatusColor}`}>
                                                {unit.status?.replace('_', ' ') || 'pending'}
                                              </span>
                                            </div>
                                            <span className="text-xs text-gray-500">{unit.completedItems}/{unit.totalItems}</span>
                                          </button>

                                          {/* Unit Body - Groups & Items */}
                                          {isUnitExpanded && (
                                            <div className="p-2 space-y-2">
                                              {unit.groups.map((group, gIdx) => {
                                                const groupCompleted = group.items.filter(i => i.isCompleted).length;
                                                return (
                                                  <div key={gIdx}>
                                                    {/* Group Header */}
                                                    <p className="text-xs font-semibold text-gray-500 bg-gray-50 px-3 py-1 rounded mb-1">
                                                      {group.groupName} ({groupCompleted}/{group.items.length})
                                                    </p>

                                                    {/* Items */}
                                                    <div className="space-y-1.5">
                                                      {group.items.map((item) => (
                                                        <div key={item.id} className={`p-2.5 rounded-lg border ${item.isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                                                          <div className="flex items-start gap-2.5">
                                                            {activeTab === 'completed' ? (
                                                              item.isCompleted ? (
                                                                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                                                              ) : (
                                                                <Circle className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" />
                                                              )
                                                            ) : (
                                                              <Checkbox
                                                                checked={item.isCompleted === true}
                                                                onCheckedChange={() => handleChecklistToggle(item.id)}
                                                                disabled={item.requiresSupervisorApproval && item.isSupervisorApproved === true}
                                                                className="mt-0.5"
                                                              />
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                              <div className="flex items-center gap-1.5 flex-wrap">
                                                                <p className={`text-sm font-medium ${item.isCompleted ? 'text-green-700' : 'text-gray-900'}`}>
                                                                  {item.item}
                                                                </p>
                                                                {item.isRequired && <span className="text-red-500 text-xs">*</span>}
                                                                {item.requiresPhoto && (
                                                                  <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">
                                                                    <Camera className="w-2.5 h-2.5" /> Photo
                                                                  </span>
                                                                )}
                                                                {item.requiresSupervisorApproval && (
                                                                  <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-orange-50 text-orange-600 border border-orange-200">
                                                                    <ShieldAlert className="w-2.5 h-2.5" /> Supervisor
                                                                  </span>
                                                                )}
                                                              </div>

                                                              {/* Approval Status */}
                                                              {item.isCompleted && item.requiresSupervisorApproval && (
                                                                <div className="mt-1">
                                                                  {item.isSupervisorApproved === true ? (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                                                      <ShieldCheck className="w-3 h-3" /> Supervisor Approved
                                                                    </span>
                                                                  ) : (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                                                                      <ShieldAlert className="w-3 h-3" /> Pending Supervisor Approval
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              )}

                                                              {/* Photo Section - always show upload for all items */}
                                                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                                {/* Upload button - hide for completed tab */}
                                                                {activeTab === 'active' && (
                                                                  <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold shadow-sm cursor-pointer transition-colors select-none">
                                                                    {uploadingPhoto === item.id ? (
                                                                      <>
                                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                                        <span>Uploading...</span>
                                                                      </>
                                                                    ) : (
                                                                      <>
                                                                        <Camera className="w-5 h-5" />
                                                                        <span>Take Photo</span>
                                                                      </>
                                                                    )}
                                                                    <input
                                                                      type="file"
                                                                      accept="image/*"
                                                                      capture="environment"
                                                                      className="hidden"
                                                                      disabled={uploadingPhoto === item.id}
                                                                      onChange={(e) => {
                                                                        if (e.target.files[0]) {
                                                                          handlePhotoUpload(selectedJob.projectId, item.id, e.target.files[0]);
                                                                          e.target.value = '';
                                                                        }
                                                                      }}
                                                                    />
                                                                  </label>
                                                                )}

                                                                {/* View photos button */}
                                                                {item.photoCount > 0 && (
                                                                  <button
                                                                    className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
                                                                    onClick={() => loadItemPhotos(selectedJob.projectId, item.id)}
                                                                  >
                                                                    <Image className="w-3 h-3" />
                                                                    {photosExpanded[item.id] ? 'Hide' : 'View'} ({item.photoCount})
                                                                  </button>
                                                                )}
                                                              </div>

                                                              {/* Photo thumbnails */}
                                                              {photosExpanded[item.id] && itemPhotos[item.id] && (
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                  {itemPhotos[item.id].map((photo) => (
                                                                    <div key={photo.id} className="relative group">
                                                                      <img
                                                                        src={photo.url}
                                                                        alt={photo.fileName}
                                                                        className="w-16 h-16 object-cover rounded border cursor-pointer hover:opacity-80"
                                                                        onClick={() => setEnlargedPhoto(photo)}
                                                                      />
                                                                      {!(item.requiresSupervisorApproval && item.isSupervisorApproved === true) && (
                                                                        <button
                                                                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                          onClick={() => handleDeletePhoto(selectedJob.projectId, photo.id, item.id)}
                                                                        >
                                                                          <X className="w-2.5 h-2.5" />
                                                                        </button>
                                                                      )}
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              )}

                                                              {/* Notes input */}
                                                              {activeTab === 'active' && (
                                                                <div className="mt-2">
                                                                  {(item.requiresSupervisorApproval && item.isSupervisorApproved === true) ? (
                                                                    item.notes && <p className="text-xs text-gray-500 italic">{item.notes}</p>
                                                                  ) : (
                                                                    <input
                                                                      type="text"
                                                                      placeholder="Add notes..."
                                                                      value={item.notes || ''}
                                                                      onChange={(e) => handleNotesChange(item.id, e.target.value)}
                                                                      className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-400"
                                                                    />
                                                                  )}
                                                                </div>
                                                              )}
                                                              {activeTab === 'completed' && item.notes && (
                                                                <p className="mt-2 text-xs text-gray-500 italic">{item.notes}</p>
                                                              )}
                                                            </div>

                                                            {/* Completed icon */}
                                                            {item.isCompleted ? (
                                                              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                            ) : (
                                                              <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                                            )}
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      No checklist items found for this project
                    </div>
                  )}

                  {activeTab === 'active' && (
                    <div className="flex gap-3 mt-6">
                      <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300"
                        onClick={handleSaveProgress}
                        disabled={savingProgress || dirtyNoteIds.size === 0}
                        title={dirtyNoteIds.size === 0 ? 'Checks auto-save. Edit a note to enable.' : `Save ${dirtyNoteIds.size} edited note(s)`}
                      >
                        {savingProgress ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        {savingProgress
                          ? 'Saving...'
                          : dirtyNoteIds.size > 0
                            ? `Save Notes (${dirtyNoteIds.size})`
                            : 'Save Notes'}
                      </Button>
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:bg-green-300"
                        onClick={handleCompleteJob}
                        disabled={completingJob || getProgress(selectedJob) < 100}
                      >
                        {completingJob ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                        )}
                        {completingJob ? 'Completing...' : 'Mark as Completed'}
                      </Button>
                    </div>
                  )}
                  {activeTab === 'completed' && (
                    <div className="mt-6 text-center">
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-green-700 bg-green-50 px-4 py-2 rounded-full">
                        <CheckCircle2 className="w-4 h-4" />
                        This job has been completed
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  Select a job to view details
                </CardContent>
              </Card>
            )}
          </div>
        </div>}

        {/* Client Review View */}
        {viewMode === 'jobs' && activeTab === 'client_review' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Project List */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Client Review Projects</h2>
              {crJobs.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center text-gray-500">No projects in client review</CardContent>
                </Card>
              ) : (
                crJobs.map(job => (
                  <Card
                    key={job.id}
                    className={`cursor-pointer transition-all border-2 ${crSelectedJob?.id === job.id ? 'border-cyan-500 bg-cyan-50/50 shadow-md' : 'border-transparent hover:shadow-md hover:border-gray-200'}`}
                    onClick={() => handleSelectCrJob(job)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold">{job.clientName}</h3>
                        <span className="text-xs px-2 py-1 rounded-full text-cyan-600 bg-cyan-50">Client Review</span>
                      </div>
                      <p className="text-sm text-gray-500">{job.oemName}</p>
                      {job.siteAddress && job.siteAddress !== 'N/A' && (
                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{job.siteAddress}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Right Panel - Checklist Detail */}
            <div className="lg:col-span-2">
              {crSelectedJob ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{crSelectedJob.clientName}</CardTitle>
                        <CardDescription>{crSelectedJob.oemName}</CardDescription>
                        {crSelectedJob.siteAddress && crSelectedJob.siteAddress !== 'N/A' && (
                          <p className="text-sm text-gray-500 mt-1">
                            <MapPin className="w-3.5 h-3.5 inline mr-1" />{crSelectedJob.siteAddress}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h3 className="font-semibold text-gray-900 mb-3">Client Review Checklist</h3>
                    {crChecklist && (
                      <div className="text-xs text-gray-500 mb-3">
                        {crChecklist.completedItems}/{crChecklist.totalItems} items completed
                      </div>
                    )}

                    {crChecklistLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
                        <span className="ml-2 text-sm text-gray-500">Loading checklist...</span>
                      </div>
                    ) : crChecklist?.groups?.length > 0 ? (
                      <div className="space-y-4">
                        {crChecklist.groups.map((group) => (
                          <div key={group.groupName}>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                              {group.groupName}
                              <span className="ml-2 text-muted-foreground normal-case">
                                ({group.items.filter(i => i.isApproved).length}/{group.items.length})
                              </span>
                            </h4>
                            <div className="space-y-1.5">
                              {group.items.map((item) => {
                                const local = crLocalChanges[item.id];
                                const effectiveCompleted = local !== undefined ? local.isCompleted : item.isCompleted;
                                const effectiveNotes = local !== undefined ? local.notes : (item.notes || '');
                                // Lock only once supervisor has approved.
                                const isLocked = item.requiresSupervisorApproval && item.isSupervisorApproved === true;
                                const projectId = crSelectedJob.projectId || crSelectedJob.id;

                                return (
                                  <div key={item.id} className={`p-2.5 rounded-lg border ${isLocked ? 'bg-green-50 border-green-200' : effectiveCompleted ? 'bg-cyan-50/50 border-cyan-200' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-start gap-2.5">
                                      {isLocked ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                                      ) : (
                                        <Checkbox
                                          checked={effectiveCompleted}
                                          onCheckedChange={() => handleCrLocalToggle(item.id)}
                                          className="mt-0.5"
                                        />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <p className={`text-sm font-medium ${isLocked ? 'text-green-700' : effectiveCompleted ? 'text-cyan-700' : 'text-gray-900'}`}>
                                            {item.itemName}
                                          </p>
                                          {item.isRequired && <span className="text-red-500 text-xs">*</span>}
                                          {item.isPhotoRequired && (
                                            <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">
                                              <Camera className="w-2.5 h-2.5" /> Photo
                                            </span>
                                          )}
                                          {item.requiresSupervisorApproval && (
                                            <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-orange-50 text-orange-600 border border-orange-200">
                                              <ShieldAlert className="w-2.5 h-2.5" /> Supervisor
                                            </span>
                                          )}
                                        </div>

                                        {/* Approval status */}
                                        {item.isCompleted && item.requiresSupervisorApproval && (
                                          <div className="mt-1">
                                            {item.isSupervisorApproved === true ? (
                                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                                <ShieldCheck className="w-3 h-3" /> Supervisor Approved
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                                                <ShieldAlert className="w-3 h-3" /> Pending Supervisor Approval
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        {isLocked && !item.requiresSupervisorApproval && (
                                          <span className="inline-flex items-center gap-1 text-[10px] text-green-600 mt-0.5">
                                            <CheckCircle2 className="w-3 h-3" /> Approved
                                          </span>
                                        )}

                                        {/* Photo upload & view */}
                                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                                          {!isLocked && (
                                            <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold shadow-sm cursor-pointer transition-colors select-none">
                                              {crUploadingPhoto === item.id ? (
                                                <>
                                                  <Loader2 className="w-5 h-5 animate-spin" />
                                                  <span>Uploading...</span>
                                                </>
                                              ) : (
                                                <>
                                                  <Camera className="w-5 h-5" />
                                                  <span>Take Photo</span>
                                                </>
                                              )}
                                              <input
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                className="hidden"
                                                disabled={crUploadingPhoto === item.id}
                                                onChange={(e) => {
                                                  if (e.target.files[0]) {
                                                    handleCrPhotoUpload(projectId, item.id, e.target.files[0]);
                                                    e.target.value = '';
                                                  }
                                                }}
                                              />
                                            </label>
                                          )}
                                          {item.photoCount > 0 && (
                                            <button
                                              className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800"
                                              onClick={() => loadCrItemPhotos(projectId, item.id)}
                                            >
                                              <Image className="w-3 h-3" />
                                              {crPhotosExpanded[item.id] ? 'Hide' : 'View'} ({item.photoCount})
                                            </button>
                                          )}
                                        </div>

                                        {/* Photo thumbnails */}
                                        {crPhotosExpanded[item.id] && crItemPhotos[item.id] && (
                                          <div className="mt-2 flex flex-wrap gap-2">
                                            {crItemPhotos[item.id].map((photo) => (
                                              <div key={photo.id} className="relative group">
                                                <img
                                                  src={photo.url}
                                                  alt={photo.fileName}
                                                  className="w-16 h-16 object-cover rounded border cursor-pointer"
                                                  onClick={() => setEnlargedPhoto(photo)}
                                                />
                                                {!isLocked && (
                                                  <button
                                                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100"
                                                    onClick={() => handleCrDeletePhoto(projectId, photo.id, item.id)}
                                                  >
                                                    <X className="w-2.5 h-2.5" />
                                                  </button>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Notes input */}
                                        {isLocked ? (
                                          item.notes && <p className="mt-1.5 text-xs text-gray-500 italic">{item.notes}</p>
                                        ) : (
                                          <input
                                            type="text"
                                            placeholder="Add notes..."
                                            value={effectiveNotes}
                                            onChange={(e) => handleCrNotesChange(item.id, e.target.value)}
                                            className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-400"
                                          />
                                        )}
                                      </div>

                                      {isLocked ? (
                                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                      ) : (
                                        <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}

                        {/* Progress bar */}
                        {crChecklist && (
                          <div className="mt-4">
                            <Progress
                              value={crChecklist.totalItems > 0 ? Math.round((crChecklist.completedItems / crChecklist.totalItems) * 100) : 0}
                              className="h-2"
                            />
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex gap-3 mt-4">
                          <Button
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300"
                            onClick={handleCrSave}
                            disabled={crSaving || Object.keys(crLocalChanges).length === 0}
                          >
                            {crSaving ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Save className="w-4 h-4 mr-2" />
                            )}
                            {crSaving
                              ? 'Saving...'
                              : Object.keys(crLocalChanges).length > 0
                                ? `Save Notes (${Object.keys(crLocalChanges).length})`
                                : 'Save Notes'}
                          </Button>
                          <Button
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            onClick={handleCompleteClientReview}
                            disabled={crCompleting || !crChecklist || crChecklist.completedItems < crChecklist.totalItems || Object.keys(crLocalChanges).length > 0}
                          >
                            {crCompleting ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                            )}
                            Complete Client Review
                          </Button>
                        </div>
                      </div>
                    ) : !crChecklistLoading && (
                      <p className="text-sm text-gray-400 text-center py-8">No checklist items configured</p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-gray-500">
                    Select a project to view client review checklist
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Enlarged Photo Dialog */}
      <Dialog open={!!enlargedPhoto} onOpenChange={() => setEnlargedPhoto(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl p-2">
          <DialogHeader>
            <DialogTitle className="text-sm">{enlargedPhoto?.fileName}</DialogTitle>
          </DialogHeader>
          {enlargedPhoto && (
            <img src={enlargedPhoto.url} alt={enlargedPhoto.fileName} className="w-full max-h-[70vh] object-contain rounded" />
          )}
        </DialogContent>
      </Dialog>

      {/* Incentive Dialog */}
      <Dialog open={incentiveDialogOpen} onOpenChange={setIncentiveDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85dvh] sm:max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Incentive Earnings</DialogTitle>
            <DialogDescription>Your incentive breakdown based on delivery performance</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg text-center">
              <p className="text-sm text-teal-700">Total Incentive Earned</p>
              <p className="text-3xl font-bold text-teal-600">₹{totalIncentive.toLocaleString()}</p>
            </div>

            <div className="space-y-3">
              {completedJobs.map(job => {
                const incentive = calculateIncentive(job);
                return (
                  <div key={job.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{job.clientName}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${incentive.status === 'Before Time' ? 'bg-green-100 text-green-700' :
                        incentive.status === 'After Time' ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                        {incentive.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{job.location}</p>
                    <p className="text-xs text-gray-400 mt-1">Project Date: {job.projectDate}</p>
                    <p className="text-xs text-gray-400">Completed: {job.completedDate}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-400">
                        {incentive.sqft} sq.ft × ₹{incentive.rate}/sq.ft
                      </p>
                      <p className="font-semibold text-teal-600">₹{incentive.amount.toLocaleString()}</p>
                    </div>
                    {job.rating && (
                      <div className="flex items-center gap-1 mt-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < job.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium mb-2">Incentive Rate Table</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-green-600">Before Delivery Date</span>
                  <span>₹25/sq.ft</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-blue-600">On Delivery Date</span>
                  <span>₹15/sq.ft</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-orange-600">After Delivery Date</span>
                  <span>₹10/sq.ft</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={attendanceDialogOpen} onOpenChange={(open) => {
        if (!open) { stopCamera(); }
        else {
          // On open: if active check-in exists, lock to that project and auto-capture GPS
          if (anyActiveCheckIn) {
            setAttendanceProjectId(anyActiveCheckIn.projectId);
            setGpsLocation(null);
            setSelfiePreview(null);
            setSelfieFile(null);
            // Auto-trigger GPS capture (same as clock-in auto-triggers after project selection)
            setTimeout(() => handleGetLocation(anyActiveCheckIn.projectId), 300);
          } else {
            setAttendanceProjectId('');
            setGpsLocation(null);
            setSelfiePreview(null);
            setSelfieFile(null);
          }
        }
        setAttendanceDialogOpen(open);
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isClockOut ? 'Clock Out' : 'Clock In'}</DialogTitle>
            <DialogDescription>{isClockOut ? 'Capture GPS and selfie to clock out' : 'Select project, capture GPS and selfie to clock in'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Project Selection */}
            <div>
              <Label>Project {isClockOut ? '' : '*'}</Label>
              {isClockOut ? (
                <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                  <MapPinned className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium">
                    {jobs.find(j => j.projectId === attendanceProjectId)?.clientName
                      || anyActiveCheckIn?.projectName
                      || 'Project'} — {jobs.find(j => j.projectId === attendanceProjectId)?.cityName || ''}
                  </span>
                  <span className="ml-auto text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full">Clocked In</span>
                </div>
              ) : (
                <Select value={attendanceProjectId} onValueChange={(v) => { setAttendanceProjectId(v); setGpsLocation(null); setSelfiePreview(null); setSelfieFile(null); stopCamera(); setTimeout(() => handleGetLocation(v), 100); }}>
                  <SelectTrigger><SelectValue placeholder="Select a project" /></SelectTrigger>
                  <SelectContent>
                    {jobs.map(j => (
                      <SelectItem key={j.projectId} value={j.projectId}>
                        {j.clientName} — {j.cityName || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium">GPS Location</p>
                <Button size="sm" onClick={() => handleGetLocation()} disabled={capturingGps || !attendanceProjectId}>
                  {capturingGps ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MapPinned className="w-4 h-4 mr-1" />}
                  {gpsLocation ? 'Recapture' : 'Capture'}
                </Button>
              </div>
              {capturingGps ? (
                <div className="p-3 rounded-lg text-sm bg-blue-50">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <p className="font-medium text-blue-700">Capturing location...</p>
                  </div>
                </div>
              ) : gpsLocation ? (
                <div className={`p-3 rounded-lg text-sm ${gpsLocation.distance <= gpsLocation.maxRadius ? 'bg-green-50' : gpsLocation.distance <= gpsLocation.maxRadius * 2 ? 'bg-yellow-50' : 'bg-red-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`font-medium ${gpsLocation.distance <= gpsLocation.maxRadius ? 'text-green-700' : gpsLocation.distance <= gpsLocation.maxRadius * 2 ? 'text-yellow-700' : 'text-red-700'}`}>
                      {gpsLocation.distance <= gpsLocation.maxRadius ? 'Within range' : gpsLocation.distance <= gpsLocation.maxRadius * 2 ? 'Slightly outside range' : 'Too far from site'}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${gpsLocation.distance <= gpsLocation.maxRadius ? 'bg-green-200 text-green-800' : gpsLocation.distance <= gpsLocation.maxRadius * 2 ? 'bg-yellow-200 text-yellow-800' : 'bg-red-200 text-red-800'
                      }`}>{gpsLocation.distance >= 1000 ? `${(gpsLocation.distance / 1000).toFixed(1)}km` : `${gpsLocation.distance.toFixed(0)}m`} from site</span>
                  </div>
                  <p className="text-gray-600">Lat: {gpsLocation.latitude.toFixed(6)}</p>
                  <p className="text-gray-600">Lng: {gpsLocation.longitude.toFixed(6)}</p>
                  {gpsLocation.distance > gpsLocation.maxRadius && (
                    <p className="text-red-600 text-xs mt-1">You must be within {gpsLocation.maxRadius}m of the project site to {isClockOut ? 'clock out' : 'clock in'}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">{attendanceProjectId ? 'Click capture to get your location' : 'Select a project first'}</p>
              )}
            </div>

            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-3">Selfie Verification</p>
              {selfiePreview ? (
                <div className="relative">
                  <img src={selfiePreview} alt="Selfie" className="w-full h-48 object-cover rounded-lg" />
                  <Button size="sm" variant="outline" className="absolute bottom-2 right-2" onClick={() => { setSelfiePreview(null); setSelfieFile(null); }}>
                    Retake
                  </Button>
                </div>
              ) : cameraActive ? (
                <div className="relative">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-48 object-cover rounded-lg bg-black" />
                  <div className="flex gap-2 justify-center mt-2">
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={capturePhoto}>
                      <Camera className="w-4 h-4 mr-1" /> Take Photo
                    </Button>
                    <Button size="sm" variant="outline" onClick={stopCamera}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 cursor-pointer"
                    onClick={startCamera}
                  >
                    <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Click to open camera</p>
                  </div>
                  {cameraError && <p className="text-xs text-red-500 mt-1">{cameraError}</p>}
                </div>
              )}
            </div>

            <Button
              className={`w-full ${isClockOut ? 'bg-orange-600 hover:bg-orange-700' : 'bg-teal-600 hover:bg-teal-700'}`}
              onClick={isClockOut ? handleClockOut : handleMarkAttendance}
              disabled={submittingAttendance || !attendanceProjectId || !gpsLocation || !selfieFile}
            >
              {submittingAttendance ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {isClockOut ? 'Confirm Clock Out' : 'Confirm Clock In'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Progress Dialog */}
      <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Progress</DialogTitle>
            <DialogDescription>
              {getCurrentTimeWindow()
                ? `Current window: ${getCurrentTimeWindow()}`
                : 'Override mode — submit anytime'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Project Selection */}
            <div>
              <Label>Project *</Label>
              <Select value={progressProjectId} onValueChange={(v) => { setProgressProjectId(v); setProgressGps(null); setTimeout(() => captureProgressGps(v), 100); }}>
                <SelectTrigger><SelectValue placeholder="Select a project" /></SelectTrigger>
                <SelectContent>
                  {jobs.filter(j => isWithinTimeWindow() || j.allowProgressReportAnytime).map(j => (
                    <SelectItem key={j.projectId} value={j.projectId}>{j.clientName} — {j.cityName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* GPS Capture */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium">GPS Location</p>
                <Button size="sm" onClick={() => captureProgressGps()} disabled={capturingProgressGps || !progressProjectId}>
                  {capturingProgressGps ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MapPinned className="w-4 h-4 mr-1" />}
                  {progressGps ? 'Recapture' : 'Capture'}
                </Button>
              </div>
              {capturingProgressGps ? (
                <div className="p-3 rounded-lg text-sm bg-blue-50">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <p className="font-medium text-blue-700">Capturing location...</p>
                  </div>
                </div>
              ) : progressGps ? (
                <div className={`p-3 rounded-lg text-sm ${progressGps.distance != null && progressGps.maxRadius != null ? (progressGps.distance <= progressGps.maxRadius ? 'bg-green-50' : progressGps.distance <= progressGps.maxRadius * 2 ? 'bg-yellow-50' : 'bg-red-50') : 'bg-green-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`font-medium ${progressGps.distance != null && progressGps.maxRadius != null ? (progressGps.distance <= progressGps.maxRadius ? 'text-green-700' : progressGps.distance <= progressGps.maxRadius * 2 ? 'text-yellow-700' : 'text-red-700') : 'text-green-700'}`}>
                      {progressGps.distance != null && progressGps.maxRadius != null ? (progressGps.distance <= progressGps.maxRadius ? 'Within range' : progressGps.distance <= progressGps.maxRadius * 2 ? 'Slightly outside range' : 'Too far from site') : 'Location captured'}
                    </p>
                    {progressGps.distance != null && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${progressGps.distance <= progressGps.maxRadius ? 'bg-green-200 text-green-800' : progressGps.distance <= progressGps.maxRadius * 2 ? 'bg-yellow-200 text-yellow-800' : 'bg-red-200 text-red-800'}`}>
                        {progressGps.distance >= 1000 ? `${(progressGps.distance / 1000).toFixed(1)}km` : `${progressGps.distance.toFixed(0)}m`} from site
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600">Lat: {progressGps.lat.toFixed(6)}</p>
                  <p className="text-gray-600">Lng: {progressGps.lng.toFixed(6)}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">{progressProjectId ? 'Click capture to get your location' : 'Select a project first'}</p>
              )}
            </div>

            {/* Photos */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium">Site Photos <span className="text-red-500">*</span></p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => progressPhotoRef.current?.click()}>
                    <Camera className="w-4 h-4 mr-1" /> Capture
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => progressUploadRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-1" /> Upload
                  </Button>
                </div>
              </div>
              <input
                ref={progressPhotoRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleProgressPhotoCapture}
              />
              <input
                ref={progressUploadRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleProgressPhotoCapture}
              />
              {progressPhotos.length > 0 ? (
                <div className="flex gap-2 flex-wrap">
                  {progressPhotos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16">
                      <img src={p.preview} alt="" className="w-full h-full object-cover rounded" />
                      <button
                        onClick={() => removeProgressPhoto(i)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                      >×</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Capture at least 1 photo of the work site</p>
              )}
            </div>

            {/* Work Description */}
            <div>
              <Label>Work Description</Label>
              <Input
                value={progressDescription}
                onChange={(e) => setProgressDescription(e.target.value)}
                placeholder="Describe work done today..."
              />
            </div>

            {/* Issues */}
            <div>
              <Label>Issues (optional)</Label>
              <Input
                value={progressIssues}
                onChange={(e) => setProgressIssues(e.target.value)}
                placeholder="Any issues or blockers..."
              />
            </div>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={handleSubmitProgressReport}
              disabled={submittingProgress || !progressProjectId || !progressGps || progressPhotos.length === 0}
            >
              {submittingProgress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Submit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85dvh] sm:max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My Profile</DialogTitle>
            <DialogDescription>Update your personal and banking details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={profileData.name} onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Age</Label>
                <Input type="number" value={profileData.age} onChange={(e) => setProfileData({ ...profileData, age: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Blood Group</Label>
                <Select value={profileData.bloodGroup} onValueChange={(v) => setProfileData({ ...profileData, bloodGroup: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {bloodGroups.map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={profileData.location} onChange={(e) => setProfileData({ ...profileData, location: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Skill Level</Label>
                <Select value={profileData.skill} onValueChange={(v) => setProfileData({ ...profileData, skill: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {skillLevels.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Designation</Label>
                <Select value={profileData.designation} onValueChange={(v) => setProfileData({ ...profileData, designation: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {designationOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="font-medium mb-3">Identity & Banking</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Aadhar Number</Label>
                  <Input value={profileData.aadhar} onChange={(e) => setProfileData({ ...profileData, aadhar: e.target.value })} placeholder="XXXX-XXXX-XXXX" />
                </div>
                <div className="space-y-2">
                  <Label>Bank Account</Label>
                  <Input value={profileData.bankAccount} onChange={(e) => setProfileData({ ...profileData, bankAccount: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input value={profileData.bankName} onChange={(e) => setProfileData({ ...profileData, bankName: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>IFSC Code</Label>
                  <Input value={profileData.ifsc} onChange={(e) => setProfileData({ ...profileData, ifsc: e.target.value })} />
                </div>
              </div>
            </div>

            <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={handleUpdateProfile}>
              Update Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div >
  );
};

export default InstallerJobs;
